/**
 * Vendor-agnostic logging abstraction.
 *
 * Application code calls logger.info/warn/error — never a vendor SDK directly.
 * The log provider determines WHERE logs are sent:
 *   - ConsoleProvider  → browser console (development)
 *   - HttpProvider     → POST to a log ingestion endpoint (production)
 *
 * To switch from DataDog to Grafana (or any vendor), change the
 * REACT_APP_LOG_ENDPOINT env var. Zero source code changes required.
 *
 * Architecture: App → logger.ts → Provider → stdout/HTTP endpoint
 * The HTTP endpoint is your vendor's log ingestion API or a collector
 * (DataDog Agent, Grafana Alloy, Fluentd, OTEL Collector).
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  service: string;
  context?: Record<string, unknown>;
}

export interface LogProvider {
  log(entry: LogEntry): void;
}

// ── Console Provider (development) ──
class ConsoleProvider implements LogProvider {
  log(entry: LogEntry): void {
    const { level, message, ...rest } = entry;
    switch (level) {
      case 'debug':
        console.debug(message, rest);
        break;
      case 'info':
        console.info(message, rest);
        break;
      case 'warn':
        console.warn(message, rest);
        break;
      case 'error':
        console.error(message, rest);
        break;
    }
  }
}

// ── HTTP Provider (production) ──
// Sends logs as JSON POST to a configurable endpoint.
// The endpoint can be DataDog's HTTP intake, Grafana Loki push API,
// or any log collector — configured via REACT_APP_LOG_ENDPOINT.
class HttpProvider implements LogProvider {
  private endpoint: string;
  private buffer: LogEntry[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly batchSize = 10;
  private readonly flushInterval = 5000;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  log(entry: LogEntry): void {
    this.buffer.push(entry);

    if (this.buffer.length >= this.batchSize) {
      this.flush();
    } else if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), this.flushInterval);
    }
  }

  private flush(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.buffer.length === 0) return;

    const batch = [...this.buffer];
    this.buffer = [];

    // Fire-and-forget — logging should never block the UI
    fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ logs: batch }),
    }).catch(() => {
      // Silently ignore log delivery failures
    });
  }
}

// ── Logger singleton ──

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

class Logger {
  private provider: LogProvider;
  private service: string;
  private minLevel: number;
  private globalContext: Record<string, unknown> = {};

  constructor() {
    const logEndpoint = process.env.REACT_APP_LOG_ENDPOINT;
    const logLevel = (process.env.REACT_APP_LOG_LEVEL || 'info') as LogLevel;

    this.service = 'wary-admin';
    this.minLevel = LOG_LEVELS[logLevel] ?? LOG_LEVELS.info;

    // If an endpoint is configured, use HTTP provider; otherwise console
    this.provider = logEndpoint
      ? new HttpProvider(logEndpoint)
      : new ConsoleProvider();
  }

  setContext(context: Record<string, unknown>): void {
    this.globalContext = { ...this.globalContext, ...context };
  }

  clearContext(): void {
    this.globalContext = {};
  }

  private log(level: LogLevel, message: string, context?: Record<string, unknown>): void {
    if (LOG_LEVELS[level] < this.minLevel) return;

    this.provider.log({
      level,
      message,
      timestamp: new Date().toISOString(),
      service: this.service,
      context: { ...this.globalContext, ...context },
    });
  }

  debug(message: string, context?: Record<string, unknown>): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: Record<string, unknown>): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: Record<string, unknown>): void {
    this.log('warn', message, context);
  }

  error(message: string, context?: Record<string, unknown>): void {
    this.log('error', message, context);
  }
}

const logger = new Logger();
export default logger;
